<template>
  <dashboard-layout :loading="initialLoading" loading-text="正在加载订单数据">
    <div class="admin-orders-page">
      <section class="summary-surface" aria-labelledby="orders-summary-title">
        <div class="revenue-summary">
          <span id="orders-summary-title">已支付金额</span>
          <strong>¥{{ formatMoney(stats.paid_amount) }}</strong>
          <small>按当前用户、渠道、套餐与下单时间范围统计</small>
        </div>

        <dl class="status-summary">
          <div>
            <dt>全部订单</dt>
            <dd>{{ formatCount(stats.total_orders) }}</dd>
          </div>
          <div>
            <dt>已支付</dt>
            <dd class="paid">{{ formatCount(stats.paid_orders) }}</dd>
          </div>
          <div>
            <dt>待支付</dt>
            <dd class="pending">{{ formatCount(stats.pending_orders) }}</dd>
          </div>
          <div>
            <dt>取消处理中</dt>
            <dd class="cancelling">{{ formatCount(stats.cancelling_orders) }}</dd>
          </div>
          <div>
            <dt>用户取消</dt>
            <dd class="cancelled">{{ formatCount(stats.cancelled_orders) }}</dd>
          </div>
          <div>
            <dt>渠道关闭</dt>
            <dd>{{ formatCount(stats.closed_orders) }}</dd>
          </div>
        </dl>

        <div class="channel-summary" aria-label="支付渠道统计">
          <div v-for="channel in normalizedChannelStats" :key="channel.channel" class="channel-row">
            <span class="channel-mark" :class="channel.channel" aria-hidden="true">
              <i :class="channel.channel === 'wechat' ? 'el-icon-chat-round' : 'el-icon-wallet'"></i>
            </span>
            <span class="channel-copy">
              <b>{{ channelLabel(channel.channel) }}</b>
              <small>{{ formatCount(channel.paid_count) }} 笔成交 / {{ formatCount(channel.order_count) }} 笔下单</small>
            </span>
            <strong>¥{{ formatMoney(channel.paid_amount) }}</strong>
          </div>
          <p v-if="!normalizedChannelStats.length" class="channel-empty">当前范围暂无渠道数据</p>
        </div>
      </section>

      <section class="filter-surface" aria-labelledby="orders-filter-title">
        <header class="section-head">
          <div>
            <h3 id="orders-filter-title">筛选订单</h3>
            <p>状态筛选仅影响下方列表，不影响上方汇总口径。</p>
          </div>
          <button type="button" class="reset-action" @click="resetFilters">重置筛选</button>
        </header>

        <div class="filters-grid">
          <label class="filter-field keyword-field">
            <span>用户或订单号</span>
            <el-input
              v-model="filters.keyword"
              clearable
              prefix-icon="el-icon-search"
              placeholder="昵称、邮箱、用户 ID、订单号"
              @keyup.enter.native="applyFilters"
            />
          </label>

          <label class="filter-field">
            <span>订单状态</span>
            <el-select v-model="filters.status" clearable placeholder="全部状态">
              <el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </label>

          <label class="filter-field">
            <span>支付方式</span>
            <el-select v-model="filters.channel" clearable placeholder="全部方式">
              <el-option label="支付宝" value="alipay" />
              <el-option label="微信支付" value="wechat" />
            </el-select>
          </label>

          <label class="filter-field">
            <span>套餐</span>
            <el-select v-model="filters.plan_code" clearable filterable placeholder="全部套餐">
              <el-option v-for="plan in plans" :key="plan.code" :label="plan.name" :value="plan.code" />
            </el-select>
          </label>

          <label class="filter-field">
            <span>计费周期</span>
            <el-select v-model="filters.period" clearable placeholder="全部周期">
              <el-option label="月付" value="monthly" />
              <el-option label="年付" value="yearly" />
            </el-select>
          </label>

          <label class="filter-field date-field">
            <span>下单时间</span>
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              unlink-panels
            />
          </label>

          <el-button type="primary" icon="el-icon-search" class="search-action" @click="applyFilters">查询</el-button>
        </div>
      </section>

      <section class="orders-surface" aria-labelledby="orders-list-title">
        <header class="list-head">
          <div>
            <h3 id="orders-list-title">订单明细</h3>
            <p aria-live="polite">共 {{ formatCount(total) }} 笔，按下单时间倒序显示</p>
          </div>
          <div class="list-head-actions">
            <span v-if="lastUpdatedAt" class="updated-at">更新于 {{ lastUpdatedAt }}</span>
            <el-button type="text" icon="el-icon-refresh" :loading="refreshing" @click="refreshData">刷新</el-button>
          </div>
        </header>

        <div class="orders-table-wrap">
          <el-table
            v-loading="tableLoading"
            :data="orders"
            row-key="out_trade_no"
            class="orders-table"
            empty-text="当前条件下没有订单"
            @row-click="openDetail"
          >
            <el-table-column label="下单时间" width="158">
              <template #default="{ row }">
                <time class="date-cell">{{ formatDate(row.created_at) }}</time>
              </template>
            </el-table-column>

            <el-table-column label="用户" min-width="210">
              <template #default="{ row }">
                <div class="user-cell">
                  <span class="user-avatar" aria-hidden="true">{{ userInitial(row) }}</span>
                  <span>
                    <b>{{ row.user_display_name || "未设置昵称" }}</b>
                    <small>{{ row.user_email || `用户 ID ${row.user_id}` }}</small>
                  </span>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="订单号" min-width="228">
              <template #default="{ row }">
                <button type="button" class="order-number" title="查看订单详情" @click.stop="openDetail(row)">
                  {{ row.out_trade_no }}
                </button>
              </template>
            </el-table-column>

            <el-table-column label="套餐" min-width="150">
              <template #default="{ row }">
                <div class="plan-cell">
                  <b>{{ row.plan_name || row.plan_code }}</b>
                  <small>{{ periodLabel(row.period) }}</small>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="金额" width="118" align="right">
              <template #default="{ row }"><strong class="amount-cell">¥{{ formatMoney(row.amount) }}</strong></template>
            </el-table-column>

            <el-table-column label="支付方式" width="116">
              <template #default="{ row }"><span class="channel-text">{{ channelLabel(row.channel) }}</span></template>
            </el-table-column>

            <el-table-column label="状态" width="126">
              <template #default="{ row }">
                <span class="status-pill" :class="statusClass(row.status)">
                  <i aria-hidden="true"></i>{{ statusLabel(row.status) }}
                </span>
              </template>
            </el-table-column>

            <el-table-column label="支付时间" width="158">
              <template #default="{ row }"><time class="date-cell">{{ formatDate(row.paid_at) }}</time></template>
            </el-table-column>

            <el-table-column label="" width="72" fixed="right" align="right">
              <template #default="{ row }">
                <button type="button" class="detail-action" aria-label="查看订单详情" @click.stop="openDetail(row)">
                  详情
                </button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <footer class="pagination-row">
          <el-pagination
            background
            :current-page="page"
            :page-size="pageSize"
            :page-sizes="[20, 50, 100]"
            :total="total"
            layout="total, sizes, prev, pager, next"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </footer>
      </section>

      <el-dialog
        title="订单详情"
        :visible.sync="detailVisible"
        width="680px"
        top="5vh"
        append-to-body
        custom-class="yw-dialog order-detail-dialog"
      >
        <div v-loading="detailLoading" class="order-detail">
          <template v-if="detailOrder">
            <header class="detail-summary">
              <div>
                <span class="status-pill" :class="statusClass(detailOrder.status)">
                  <i aria-hidden="true"></i>{{ statusLabel(detailOrder.status) }}
                </span>
                <h3>{{ detailOrder.plan_name || detailOrder.plan_code }}</h3>
                <p>{{ detailOrder.out_trade_no }}</p>
              </div>
              <strong>¥{{ formatMoney(detailOrder.amount) }}</strong>
            </header>

            <dl class="detail-grid">
              <div><dt>用户</dt><dd>{{ detailOrder.user_display_name || "未设置昵称" }}</dd></div>
              <div><dt>邮箱</dt><dd>{{ detailOrder.user_email || "—" }}</dd></div>
              <div><dt>用户 ID</dt><dd>{{ detailOrder.user_id || "—" }}</dd></div>
              <div><dt>支付方式</dt><dd>{{ channelLabel(detailOrder.channel) }}</dd></div>
              <div><dt>计费周期</dt><dd>{{ periodLabel(detailOrder.period) }}</dd></div>
              <div><dt>第三方流水号</dt><dd>{{ detailOrder.trade_no || "—" }}</dd></div>
              <div><dt>下单时间</dt><dd>{{ formatDate(detailOrder.created_at) }}</dd></div>
              <div><dt>支付时间</dt><dd>{{ formatDate(detailOrder.paid_at) }}</dd></div>
              <div><dt>更新时间</dt><dd>{{ formatDate(detailOrder.updated_at) }}</dd></div>
              <div><dt>取消申请时间</dt><dd>{{ formatDate(detailOrder.cancel_requested_at) }}</dd></div>
              <div><dt>取消完成时间</dt><dd>{{ formatDate(detailOrder.cancelled_at) }}</dd></div>
              <div><dt>渠道关闭状态</dt><dd>{{ channelCloseLabel(detailOrder.channel_close_status) }}</dd></div>
            </dl>

            <section v-if="hasCancellationDetail(detailOrder)" class="cancel-detail">
              <div>
                <span>取消处理</span>
                <strong>{{ formatCount(detailOrder.cancel_attempts) }} 次尝试</strong>
              </div>
              <p v-if="detailOrder.cancel_reason">原因：{{ cancelReasonLabel(detailOrder.cancel_reason) }}</p>
              <p v-if="detailOrder.channel_close_error">渠道反馈：{{ detailOrder.channel_close_error }}</p>
              <p v-if="detailOrder.next_cancel_retry_at">下次重试：{{ formatDate(detailOrder.next_cancel_retry_at) }}</p>
            </section>
          </template>
        </div>
        <span slot="footer"><el-button @click="detailVisible = false">关闭</el-button></span>
      </el-dialog>
    </div>
  </dashboard-layout>
</template>

<script>
import DashboardLayout from "@/components/DashboardLayout.vue";
import { adminApi } from "@/services/api";

const EMPTY_STATS = Object.freeze({
  total_orders: 0,
  paid_orders: 0,
  pending_orders: 0,
  cancelling_orders: 0,
  cancelled_orders: 0,
  closed_orders: 0,
  paid_amount: "0.00",
  channel_stats: [],
});

const STATUS_LABELS = Object.freeze({
  paid: "已支付",
  pending: "待支付",
  cancelling: "取消处理中",
  cancelled: "已取消",
  closed: "已关闭",
});

export default {
  name: "AdminOrdersView",
  components: { DashboardLayout },
  data() {
    return {
      initialLoading: true,
      tableLoading: false,
      refreshing: false,
      listRequestSequence: 0,
      statsRequestSequence: 0,
      detailRequestSequence: 0,
      orders: [],
      plans: [],
      stats: { ...EMPTY_STATS },
      total: 0,
      page: 1,
      pageSize: 20,
      pages: 0,
      filters: {
        keyword: "",
        status: "",
        channel: "",
        plan_code: "",
        period: "",
      },
      dateRange: [],
      lastUpdatedAt: "",
      detailVisible: false,
      detailLoading: false,
      detailOrder: null,
      statusOptions: [
        { label: "已支付", value: "paid" },
        { label: "待支付", value: "pending" },
        { label: "取消处理中", value: "cancelling" },
        { label: "已取消", value: "cancelled" },
        { label: "已关闭", value: "closed" },
      ],
    };
  },
  computed: {
    normalizedChannelStats() {
      return Array.isArray(this.stats.channel_stats) ? this.stats.channel_stats : [];
    },
  },
  async created() {
    await Promise.all([this.loadPlans(), this.loadData()]);
    this.initialLoading = false;
  },
  methods: {
    async loadPlans() {
      try {
        this.plans = (await adminApi.subscriptionPlans()) || [];
      } catch (error) {
        this.plans = [];
      }
    },
    buildFilterParams({ includeStatus = true } = {}) {
      const params = {};
      Object.entries(this.filters).forEach(([key, value]) => {
        const normalized = typeof value === "string" ? value.trim() : value;
        if (normalized && (includeStatus || key !== "status")) params[key] = normalized;
      });
      if (Array.isArray(this.dateRange) && this.dateRange.length === 2) {
        params.created_from = this.dateRange[0];
        params.created_to = this.dateRange[1];
      }
      return params;
    },
    async loadData({ includeStats = true } = {}) {
      await Promise.all([
        this.loadOrders(),
        includeStats ? this.loadStats() : Promise.resolve(),
      ]);
    },
    async loadOrders() {
      const sequence = ++this.listRequestSequence;
      this.tableLoading = true;
      try {
        const listParams = {
          ...this.buildFilterParams(),
          page: this.page,
          page_size: this.pageSize,
        };
        const listResult = await adminApi.paymentOrders(listParams);
        if (sequence !== this.listRequestSequence) return;
        this.orders = Array.isArray(listResult.items) ? listResult.items : [];
        this.total = Number(listResult.total) || 0;
        this.page = Number(listResult.page) || 1;
        this.pageSize = Number(listResult.page_size) || this.pageSize;
        this.pages = Number(listResult.pages) || 0;
        this.lastUpdatedAt = new Date().toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
      } catch (error) {
        if (sequence === this.listRequestSequence) this.$message.error(error.message || "订单数据加载失败");
      } finally {
        if (sequence === this.listRequestSequence) this.tableLoading = false;
      }
    },
    async loadStats() {
      const sequence = ++this.statsRequestSequence;
      try {
        const statsResult = await adminApi.paymentOrderStats(
          this.buildFilterParams({ includeStatus: false })
        );
        if (sequence !== this.statsRequestSequence) return;
        this.stats = { ...EMPTY_STATS, ...(statsResult || {}) };
      } catch (error) {
        if (sequence === this.statsRequestSequence) this.$message.error(error.message || "订单统计加载失败");
      }
    },
    async refreshData() {
      this.refreshing = true;
      try {
        await this.loadData();
      } finally {
        this.refreshing = false;
      }
    },
    applyFilters() {
      this.page = 1;
      this.loadData();
    },
    resetFilters() {
      this.filters = { keyword: "", status: "", channel: "", plan_code: "", period: "" };
      this.dateRange = [];
      this.page = 1;
      this.loadData();
    },
    handleSizeChange(value) {
      this.pageSize = value;
      this.page = 1;
      this.loadData({ includeStats: false });
    },
    handlePageChange(value) {
      this.page = value;
      this.loadData({ includeStats: false });
    },
    async openDetail(row) {
      if (!row || !row.out_trade_no) return;
      const sequence = ++this.detailRequestSequence;
      this.detailOrder = { ...row };
      this.detailVisible = true;
      this.detailLoading = true;
      try {
        const detail = await adminApi.paymentOrderDetail(row.out_trade_no);
        if (sequence !== this.detailRequestSequence) return;
        this.detailOrder = detail;
      } catch (error) {
        if (sequence === this.detailRequestSequence) this.$message.error(error.message || "订单详情加载失败");
      } finally {
        if (sequence === this.detailRequestSequence) this.detailLoading = false;
      }
    },
    formatCount(value) {
      return (Number(value) || 0).toLocaleString("zh-CN");
    },
    formatMoney(value) {
      const amount = Number(value);
      if (!Number.isFinite(amount)) return "0.00";
      return amount.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleString("zh-CN", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
      }
      return String(value).replace("T", " ").slice(0, 16);
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status || "未知";
    },
    statusClass(status) {
      return STATUS_LABELS[status] ? status : "unknown";
    },
    channelLabel(channel) {
      return channel === "wechat" ? "微信支付" : channel === "alipay" ? "支付宝" : channel || "—";
    },
    periodLabel(period) {
      return period === "yearly" ? "年付" : period === "monthly" ? "月付" : period || "—";
    },
    channelCloseLabel(status) {
      const labels = {
        processing: "正在处理",
        confirmed: "渠道已关闭",
        failed: "渠道关闭失败",
        unsupported: "渠道不支持主动关闭",
      };
      return labels[status] || status || "—";
    },
    cancelReasonLabel(reason) {
      return reason === "user_cancel" ? "用户主动取消" : reason || "—";
    },
    userInitial(row) {
      return (row.user_display_name || row.user_email || "用").trim().slice(0, 1).toUpperCase();
    },
    hasCancellationDetail(order) {
      return !!(
        order.cancel_requested_at ||
        order.cancelled_at ||
        order.cancel_reason ||
        order.channel_close_error ||
        order.cancel_attempts ||
        order.next_cancel_retry_at
      );
    },
  },
};
</script>

<style scoped lang="scss">
.admin-orders-page {
  width: 100%;
  max-width: 1480px;
  margin: 0 auto;
  color: var(--xyo-ink);

  .summary-surface,
  .filter-surface,
  .orders-surface {
    border: 1px solid var(--xyo-line);
    border-radius: var(--xyo-radius-md);
    background: var(--xyo-surface-raised);
  }

  .summary-surface {
    display: grid;
    grid-template-columns: minmax(220px, 0.8fr) minmax(500px, 1.8fr) minmax(280px, 1fr);
    margin-bottom: 18px;
    overflow: hidden;
    animation: orders-enter 260ms ease-out both;
  }

  .revenue-summary {
    display: flex;
    min-height: 162px;
    flex-direction: column;
    justify-content: center;
    padding: 24px 28px;
    border-right: 1px solid var(--xyo-line);
    background: var(--xyo-accent-pale);

    span {
      color: var(--xyo-muted);
      font-size: 12px;
      font-weight: 650;
    }

    strong {
      margin: 8px 0 6px;
      color: var(--xyo-ink-strong);
      font-size: clamp(30px, 3vw, 44px);
      font-weight: 760;
      line-height: 1;
      letter-spacing: -0.04em;
      font-variant-numeric: tabular-nums;
    }

    small {
      color: var(--xyo-muted);
      font-size: 10px;
      line-height: 1.5;
    }
  }

  .status-summary {
    display: grid;
    grid-template-columns: repeat(3, minmax(100px, 1fr));
    margin: 0;

    > div {
      display: flex;
      min-height: 81px;
      flex-direction: column;
      justify-content: center;
      padding: 14px 20px;
      border-right: 1px solid var(--xyo-line);
      border-bottom: 1px solid var(--xyo-line);

      &:nth-child(n + 4) {
        border-bottom: 0;
      }
    }

    dt {
      color: var(--xyo-muted);
      font-size: 11px;
    }

    dd {
      margin: 5px 0 0;
      color: var(--xyo-ink-strong);
      font-size: 22px;
      font-weight: 730;
      font-variant-numeric: tabular-nums;

      &.paid { color: var(--xyo-success); }
      &.pending { color: var(--xyo-warning); }
      &.cancelling { color: var(--xyo-info); }
      &.cancelled { color: var(--xyo-danger); }
    }
  }

  .channel-summary {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 14px 20px;

    .channel-row {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr) auto;
      align-items: center;
      gap: 10px;
      padding: 10px 0;

      + .channel-row {
        border-top: 1px solid var(--xyo-line);
      }
    }

    .channel-mark {
      display: grid;
      width: 34px;
      height: 34px;
      place-items: center;
      border-radius: 9px;
      color: var(--xyo-info);
      background: var(--xyo-info-soft);

      &.wechat {
        color: var(--xyo-success);
        background: var(--xyo-success-soft);
      }
    }

    .channel-copy {
      display: flex;
      min-width: 0;
      flex-direction: column;

      b {
        color: var(--xyo-ink-strong);
        font-size: 12px;
      }

      small {
        margin-top: 2px;
        overflow: hidden;
        color: var(--xyo-muted);
        font-size: 10px;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .channel-row > strong {
      color: var(--xyo-ink-strong);
      font-size: 13px;
      font-variant-numeric: tabular-nums;
    }

    .channel-empty {
      margin: 0;
      color: var(--xyo-muted);
      font-size: 12px;
      text-align: center;
    }
  }

  .filter-surface {
    margin-bottom: 18px;
    padding: 18px 20px 20px;
    animation: orders-enter 300ms 40ms ease-out both;
  }

  .section-head,
  .list-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;

    h3 {
      margin: 0;
      color: var(--xyo-ink-strong);
      font-size: 15px;
      font-weight: 720;
    }

    p {
      margin: 4px 0 0;
      color: var(--xyo-muted);
      font-size: 11px;
    }
  }

  .reset-action,
  .detail-action,
  .order-number {
    padding: 0;
    border: 0;
    color: var(--xyo-primary);
    background: transparent;
    font: inherit;
    cursor: pointer;
  }

  .reset-action {
    min-height: 30px;
    font-size: 12px;
  }

  .filters-grid {
    display: grid;
    grid-template-columns: minmax(220px, 1.35fr) repeat(4, minmax(128px, 0.7fr)) minmax(250px, 1.2fr) auto;
    align-items: end;
    gap: 12px;
    margin-top: 16px;
  }

  .filter-field {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 7px;
    color: var(--xyo-muted);
    font-size: 11px;
    font-weight: 650;

    ::v-deep .el-select,
    ::v-deep .el-date-editor {
      width: 100%;
    }
  }

  .search-action {
    min-width: 86px;
  }

  .orders-surface {
    overflow: hidden;
    animation: orders-enter 340ms 80ms ease-out both;
  }

  .list-head {
    align-items: center;
    padding: 18px 20px;
    border-bottom: 1px solid var(--xyo-line);

    .updated-at {
      color: var(--xyo-muted);
      font-size: 10px;
    }
  }

  .list-head-actions {
    display: flex;
    align-items: center;
    gap: 14px;

    ::v-deep .el-button {
      padding: 6px 0;
      font-size: 11px;
    }
  }

  .orders-table-wrap {
    width: 100%;
    min-width: 0;
    overflow: hidden;
  }

  ::v-deep .orders-table {
    width: 100%;
    color: var(--xyo-ink);

    &::before {
      display: none;
    }

    th.el-table__cell {
      height: 42px;
      color: var(--xyo-muted);
      background: var(--xyo-surface-muted) !important;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.04em;
    }

    td.el-table__cell {
      height: 60px;
      padding-top: 8px;
      padding-bottom: 8px;
    }

    .el-table__row {
      cursor: pointer;
      transition: background-color 160ms ease;
    }

    .el-table__row:hover > td.el-table__cell {
      background: var(--xyo-accent-pale) !important;
    }

    .el-table__fixed-right::before {
      display: none;
    }
  }

  .date-cell {
    color: var(--xyo-ink-soft);
    font-size: 11px;
    font-variant-numeric: tabular-nums;
  }

  .user-cell {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;

    .user-avatar {
      display: grid;
      flex: 0 0 34px;
      width: 34px;
      height: 34px;
      place-items: center;
      border-radius: 10px;
      color: var(--xyo-accent);
      background: var(--xyo-accent-soft);
      font-size: 12px;
      font-weight: 750;
    }

    > span:last-child {
      display: flex;
      min-width: 0;
      flex-direction: column;

      b,
      small {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      b {
        color: var(--xyo-ink-strong);
        font-size: 12px;
      }

      small {
        margin-top: 2px;
        color: var(--xyo-muted);
        font-size: 10px;
      }
    }
  }

  .order-number {
    max-width: 100%;
    overflow: hidden;
    font-family: "SFMono-Regular", Consolas, monospace;
    font-size: 11px;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .plan-cell {
    display: flex;
    min-width: 0;
    flex-direction: column;

    b {
      overflow: hidden;
      color: var(--xyo-ink-strong);
      font-size: 12px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    small {
      margin-top: 2px;
      color: var(--xyo-muted);
      font-size: 10px;
    }
  }

  .amount-cell {
    color: var(--xyo-ink-strong);
    font-size: 13px;
    font-variant-numeric: tabular-nums;
  }

  .channel-text {
    color: var(--xyo-ink-soft);
    font-size: 11px;
  }

  .status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border-radius: 999px;
    color: var(--xyo-muted);
    background: var(--xyo-surface-muted);
    font-size: 10px;
    font-weight: 650;
    white-space: nowrap;

    i {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: currentColor;
    }

    &.paid { color: var(--xyo-success); background: var(--xyo-success-soft); }
    &.pending { color: var(--xyo-warning); background: var(--xyo-warning-soft); }
    &.cancelling { color: var(--xyo-info); background: var(--xyo-info-soft); }
    &.cancelled,
    &.closed { color: var(--xyo-danger); background: var(--xyo-danger-soft); }
  }

  .detail-action {
    min-height: 30px;
    font-size: 11px;
    font-weight: 650;
  }

  .pagination-row {
    display: flex;
    justify-content: flex-end;
    padding: 16px 20px;
    border-top: 1px solid var(--xyo-line);
  }

}

@keyframes orders-enter {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 1260px) {
  .admin-orders-page {
    .summary-surface {
      grid-template-columns: minmax(220px, 0.7fr) minmax(500px, 1.6fr);
    }

    .channel-summary {
      grid-column: 1 / -1;
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      padding-top: 0;
      padding-bottom: 0;
      border-top: 1px solid var(--xyo-line);

      .channel-row {
        padding: 14px 20px;

        + .channel-row {
          border-top: 0;
          border-left: 1px solid var(--xyo-line);
        }
      }
    }

    .filters-grid {
      grid-template-columns: minmax(220px, 1.4fr) repeat(3, minmax(135px, 0.75fr));

      .date-field {
        grid-column: span 2;
      }
    }
  }
}

@media (max-width: 900px) {
  .admin-orders-page {
    .summary-surface {
      grid-template-columns: 1fr;
    }

    .revenue-summary {
      min-height: 130px;
      border-right: 0;
      border-bottom: 1px solid var(--xyo-line);
    }

    .status-summary > div:nth-child(3n) {
      border-right: 0;
    }

    .filters-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));

      .keyword-field,
      .date-field {
        grid-column: 1 / -1;
      }

      .search-action {
        grid-column: 2;
      }
    }
  }
}

@media (max-width: 640px) {
  .admin-orders-page {
    .status-summary {
      grid-template-columns: repeat(2, minmax(0, 1fr));

      > div,
      > div:nth-child(n + 4) {
        border-right: 1px solid var(--xyo-line);
        border-bottom: 1px solid var(--xyo-line);
      }

      > div:nth-child(even) {
        border-right: 0;
      }

      > div:nth-last-child(-n + 2) {
        border-bottom: 0;
      }
    }

    .channel-summary {
      grid-template-columns: 1fr;

      .channel-row + .channel-row {
        border-top: 1px solid var(--xyo-line);
        border-left: 0;
      }
    }

    .section-head,
    .list-head {
      align-items: flex-start;
      flex-direction: column;
      gap: 8px;
    }

    .list-head-actions {
      width: 100%;
      justify-content: space-between;
    }

    .filters-grid {
      grid-template-columns: 1fr;

      .keyword-field,
      .date-field,
      .search-action {
        grid-column: 1;
      }
    }

    .filter-surface {
      padding-right: 14px;
      padding-left: 14px;
    }

    .pagination-row {
      justify-content: flex-start;
      overflow-x: auto;
    }

  }
}

@media (prefers-reduced-motion: reduce) {
  .admin-orders-page .summary-surface,
  .admin-orders-page .filter-surface,
  .admin-orders-page .orders-surface {
    animation: none;
  }
}
</style>

<style lang="scss">
.order-detail-dialog {
  display: flex;
  max-height: 90vh;
  flex-direction: column;
  margin-bottom: 5vh;

  .el-dialog__body {
    overflow-y: auto;
  }

  .order-detail {
    min-height: 180px;
  }

  .status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border-radius: 999px;
    color: var(--xyo-muted);
    background: var(--xyo-surface-muted);
    font-size: 10px;
    font-weight: 650;
    white-space: nowrap;

    i {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: currentColor;
    }

    &.paid { color: var(--xyo-success); background: var(--xyo-success-soft); }
    &.pending { color: var(--xyo-warning); background: var(--xyo-warning-soft); }
    &.cancelling { color: var(--xyo-info); background: var(--xyo-info-soft); }
    &.cancelled,
    &.closed { color: var(--xyo-danger); background: var(--xyo-danger-soft); }
  }

  .detail-summary {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    padding: 4px 0 18px;
    border-bottom: 1px solid var(--xyo-line);

    h3 {
      margin: 10px 0 3px;
      color: var(--xyo-ink-strong);
      font-size: 19px;
    }

    p {
      margin: 0;
      color: var(--xyo-muted);
      font-family: "SFMono-Regular", Consolas, monospace;
      font-size: 10px;
    }

    > strong {
      color: var(--xyo-ink-strong);
      font-size: 26px;
      font-variant-numeric: tabular-nums;
    }
  }

  .detail-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 24px;
    margin: 8px 0 0;

    > div {
      min-width: 0;
      padding: 11px 0;
      border-bottom: 1px solid var(--xyo-line);
    }

    dt {
      margin-bottom: 4px;
      color: var(--xyo-muted);
      font-size: 10px;
    }

    dd {
      margin: 0;
      overflow-wrap: anywhere;
      color: var(--xyo-ink-strong);
      font-size: 12px;
    }
  }

  .cancel-detail {
    margin-top: 16px;
    padding: 14px 16px;
    border-left: 3px solid var(--xyo-info);
    border-radius: 0 var(--xyo-radius-sm) var(--xyo-radius-sm) 0;
    background: var(--xyo-info-soft);

    > div {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      color: var(--xyo-info);
      font-size: 11px;
    }

    p {
      margin: 7px 0 0;
      color: var(--xyo-ink-soft);
      font-size: 11px;
      line-height: 1.55;
    }
  }
}

@media (max-width: 640px) {
  .order-detail-dialog {
    .detail-summary {
      flex-direction: column;
    }

    .detail-grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>
